import DesignButton from 'components/button/DesignButton';
import DesignCard from 'components/card/DesignCard';
import FileDropzone from 'components/fileDropzone/FileDropzone';
import { BackIcon, SpinnerIcon } from 'components/svgs';
import {
  ACCEPTED_FILE_ACCEPT,
  LARGE_FILE_NOTE,
  validateCapacityPlanFile,
} from 'core/apis/capacityPlanning';
import useAppDispatch from 'core/hooks/useAppDispatch';
import useAppSelector from 'core/hooks/useAppSelector';
import {
  patchSettingsDraft,
  selectSettingsDraft,
} from 'core/store/CapacityPlanningSlice';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { draftToSettings, labelMaxLength } from './RunSettings';
import RunSettingsForm from './RunSettingsForm';
import TemplateDownloads from './TemplateDownloads';
import useCapacityPlanUpload from './useCapacityPlanUpload';

/** Columns every uploaded file must carry, whatever its column order. */
const requiredColumns = [
  'Agency',
  'Address',
  'Town',
  'Village',
  'County',
  'Latitude',
  'Longitude',
  'DepotLatitude',
  'DepotLongitude',
  'DeliveryId',
  'DeliveryTime',
  'ParkingTime',
  'ApproximateLocationAdditionalTime',
  'IsApproximated',
];

/** Columns the file may carry; the Run reads them when they are there. */
const optionalColumns = ['RecipientPhone'];

/** Columns the file may carry that the Run accepts and does not read. */
const ignoredColumns = ['Cerc Logistic', 'Retea'];

const megaBytes = (bytes: number) => (bytes / (1024 * 1024)).toFixed(1);

export interface NewRunProps {
  /** Guid of the Agency the Run belongs to; null for a Network Run. */
  agencyId: string | null;
  agencyName: string;
  /** A Network Run belongs to the country and reads its depots from the file. */
  isNetworkRun: boolean;
  canEdit: boolean;
  /**
   * Hands the new Run to the shell, which opens it and refetches the Run list.
   * Called only after the upload succeeded, and its own failure is reported
   * separately: the Run exists either way.
   */
  onCreated: (runId: string) => void | Promise<void>;
  onCancel: () => void;
}

/**
 * Creating a Run is one form: the Run Settings plus the deliveries file, and a
 * single "Create run" button. The Run is created by the upload itself, so its
 * id only exists once the bytes are in.
 */
const NewRun = ({
  agencyId,
  agencyName,
  isNetworkRun,
  canEdit,
  onCreated,
  onCancel,
}: NewRunProps) => {
  const dispatch = useAppDispatch();
  const draft = useAppSelector(selectSettingsDraft);
  const [file, setFile] = useState<File | null>(null);
  const { isUploading, progress, start, cancel } = useCapacityPlanUpload();

  // The Label defaults to the file name, and only until the supervisor types
  // one of their own.
  const pickFile = (picked: File | null) => {
    setFile(picked);
    if (picked && draft.label.trim() === '') {
      dispatch(
        patchSettingsDraft({ label: picked.name.slice(0, labelMaxLength) }),
      );
    }
  };

  const handleCreate = async () => {
    if (!file) {
      return;
    }

    const fileError = validateCapacityPlanFile(file);
    if (fileError) {
      toast.error(fileError);
      return;
    }

    const converted = draftToSettings(draft, { isNetworkRun });
    if (!converted.ok) {
      toast.error(`Check the settings: ${converted.message}`);
      return;
    }

    const runId = await start({
      file,
      settings: converted.settings,
      agencyId,
      label: converted.settings.label ?? null,
    });
    if (!runId) {
      return;
    }

    // The Run exists from here on, so the file is dropped: uploading the same
    // one again would create a second Run.
    setFile(null);
    toast.success('The file was uploaded. The run opens as soon as it parses.');

    // Opening the Run refetches the Run list. That is the shell's own work, so
    // its failure must not read as a failed upload - which would invite the
    // supervisor to upload the very same file again.
    try {
      await onCreated(runId);
    } catch {
      toast.error(
        'The run was created, but the list could not be refreshed. Reload the page.',
      );
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold">New run</h3>
          <DesignButton
            variant="ghost"
            onClickHandler={onCancel}
            // Leaving mid-upload unmounts the form that owns the request; the
            // upload has its own Cancel button for that.
            disabled={isUploading}
          >
            <BackIcon />
            Back to runs
          </DesignButton>
        </div>
        <p className="text-sm text-light-black dark:text-sublte-white">
          {isNetworkRun ? 'Network run' : `Agency ${agencyName}`} · settings
          below are saved with the run and prefilled from the latest one.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2">
          <RunSettingsForm disabled={!canEdit || isUploading} layout="stack" />
        </div>
        <div className="self-start">
          <DesignCard title="Deliveries file" className="sticky top-8">
            <FileDropzone
              inputId="capacityPlanFile"
              accept={ACCEPTED_FILE_ACCEPT}
              file={file}
              onFileChange={pickFile}
              disabled={!canEdit || isUploading}
              hint={LARGE_FILE_NOTE}
            />

            <div className="mt-4 text-xs text-light-black dark:text-sublte-white">
              <div className="font-semibold">Required columns</div>
              <div>{requiredColumns.join(', ')}</div>
              <div className="mt-1 font-semibold">Optional columns</div>
              <div>{optionalColumns.join(', ')}</div>
              <div className="mt-1 font-semibold">Accepted and ignored</div>
              <div>{ignoredColumns.join(', ')}</div>
              <div className="mt-1">
                Leave both delivery coordinates empty to geocode a row from its
                address.
              </div>
            </div>

            <div className="mt-3">
              <TemplateDownloads isNetworkRun={isNetworkRun} />
            </div>

            <hr className="my-4 border-design-secondary-black" />

            {isUploading && (
              <div
                role="status"
                aria-live="polite"
                className="mb-3 flex items-center gap-2 text-sm"
              >
                <SpinnerIcon className="h-5 w-5 text-yellow" />
                <span>Uploading…</span>
              </div>
            )}

            {progress && (
              <div className="mb-3 flex flex-col gap-1">
                <div
                  role="progressbar"
                  aria-label="Upload progress"
                  aria-valuenow={progress.percentage}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  className="h-2 overflow-hidden rounded-sm bg-design-secondary-black"
                >
                  <div
                    className="h-2 rounded-sm bg-design-green"
                    style={{ width: `${progress.percentage}%` }}
                  />
                </div>
                <span className="text-xs text-light-black dark:text-sublte-white">
                  {/* The file is compressed before anything is sent, and the
                  bytes then counted are the compressed ones, so the two phases
                  are told apart rather than shown on one scale. */}
                  {progress.bytesUploaded === 0
                    ? `Compressing ${megaBytes(progress.bytesTotal)} MB…`
                    : `${progress.percentage}% · ${megaBytes(progress.bytesUploaded)} of ${megaBytes(progress.bytesTotal)} MB sent`}
                </span>
              </div>
            )}

            {isUploading ? (
              <DesignButton
                variant="secondary"
                className="w-full"
                onClickHandler={cancel}
              >
                Cancel upload
              </DesignButton>
            ) : (
              <DesignButton
                variant="primary"
                className="w-full"
                disabled={!canEdit || !file}
                onClickHandler={handleCreate}
              >
                Create run
              </DesignButton>
            )}
            <p className="mt-2 text-xs text-light-black dark:text-sublte-white">
              Uploads the file; the run reads the rows and then opens here.
            </p>
          </DesignCard>
        </div>
      </div>
    </div>
  );
};

export default NewRun;
